import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Projektmanagement";
const heroImage = "https://net-basis.net/Bilder/project-management-planning-control-dach-market-entry.webp";
const heroAlt = "Geschäftsmann erstellt ein Ablaufdiagramm für sicheres Projektmanagement beim Markteintritt.";
const heroClaimLine1 = "Perfekt geplant!";
const heroClaimLine2 = "Sicher gesteuert!";
const widget2 = "Ihr erfahrener Partner für umfassende Projektberatung und direktes Projektmanagement in der DACH-Region. Wir steuern komplexe Markteintrittsprojekte ausländischer Hersteller zielorientiert, risikobewusst und mit tiefer lokaler Expertise.";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Projektmanagement__ profitieren:";
const p1 = "NetBasis International unterstützt seine Herstellerpartner bei ihrem Markteintritt auf Wunsch mit umfassender Projektberatung und direktem Projektmanagement. Denn gerade der Plan, in neuen Märkten präsent sein zu wollen, ist in den meisten Fällen eine strategische und somit sehr wichtige Entscheidung. Ein Scheitern muss dabei unbedingt vermieden werden. Gleichzeitig ist ein solches Vorhaben oft extrem komplex und verlangt ein höheres Maß an marktspezifischer Expertise, fachlicher Kompetenz und auch gezielter Schwerpunktausrichtung, als einem Unternehmen, das nicht vor Ort ist, typischerweise zur Verfügung stehen.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Zuverlässige Übernahme von Planung, Überwachung und Steuerung eines Markteintrittsprojekts",
  "Strukturierte Hilfe bei der Abstimmung von Leistungsumfang, Projektterminen, Kosten, Finanzierung und Ressourcen",
  "Fachgerechte Durchführung des gesamten lokalen Projektcontrollings vor Ort",
  "Fundierte Unterstützung bei der Identifizierung, Analyse, Bewertung und Beherrschung von Projektrisiken",
  "Kontinuierliche Beobachtung des aktuellen Projektfortschritts und präziser Vergleich mit der Planung",
  "Lückenlose Überwachung von Termintreue, inhaltlichem Fortschritt und Kostenbudgets",
  "Permanente Projektinformation und transparente Kommunikation mit dem Herstellerpartner",
  "Kompetente Assistenz bei allen landes- und marktspezifischen Rechtsangelegenheiten",
];

export default function ProjektmanagementDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Projektmanagement | NetBasis International"
        description="Effiziente Steuerung und operative Umsetzung Ihrer Markteintritts- und Vertriebsprojekte durch erfahrene Branchenexperten."
        ogUrl="https://net-basis.net/de/services/business-development/projektmanagement"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <h1 className="text-sm md:text-base font-semibold text-foreground-950 mb-4">{widget2}</h1>
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}